import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight, ShieldCheck, MailCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import FoxLogo from '../components/FoxLogo';
import { apiService } from '../services/api';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage(); // Dil fonksiyonunu al
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState(t('verify_checking') || 'E-Mail wird überprüft...');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t('verify_missing_token') || 'Verification token missing or invalid.');
        return;
      }

      try {
        await apiService.auth.verifyEmail(token);
        setStatus('success');
        setMessage(t('verify_success_msg') || 'Your email address has been successfully verified.');
      } catch (error) {
        setStatus('error');
        // Backend'den gelen hata mesajı varsa göster, yoksa varsayılan çeviriyi göster
        setMessage(error.response?.data?.detail || t('verify_error_msg_default') || 'Link expired or already used.');
      }
    };

    const timer = setTimeout(verify, 1500); // Kullanıcıya işlemi hissettirmek için kısa bir delay
    return () => clearTimeout(timer);
  }, [token, t]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8fafc] dark:bg-[#070b14] p-6 font-sans relative overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-tr from-blue-500/5 to-orange-500/5 blur-[120px]"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white dark:bg-[#111827] p-10 rounded-[3rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] dark:shadow-none border border-slate-100 dark:border-white/5 ring-1 ring-slate-200/50 dark:ring-white/5 text-center">
          
          <div className="flex justify-center mb-10 relative">
             <div className="p-4 bg-slate-50 dark:bg-white/5 rounded-3xl relative">
                <FoxLogo size={60} />
             </div>
             {status === 'success' && (
                <motion.div 
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="absolute -right-2 -bottom-2 bg-emerald-500 text-white p-2 rounded-full border-4 border-white dark:border-[#111827] shadow-lg"
                >
                  <ShieldCheck size={20} />
                </motion.div>
             )}
          </div>

          <AnimatePresence mode="wait">
            {status === 'loading' && (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div className="flex justify-center">
                   <Loader2 size={40} className="text-orange-500 animate-spin" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic">{t('verify_title') || 'Verifying'}</h2>
                  <p className="text-slate-500 font-medium text-sm">{t('verify_subtitle') || 'BüroFuchs is verifying your identity...'}</p>
                </div>
              </motion.div>
            )}

            {status === 'success' && (
              <motion.div 
                key="success"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="space-y-8"
              >
                <div className="space-y-3">
                   <Button variant="ghost" className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 border-0 px-4 py-1 h-8 text-[10px] font-black uppercase tracking-widest pointer-events-none">
                      {t('verify_complete_badge') || 'Verification Complete'}
                   </Button>
                   <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
                      {t('verify_success_title') || 'Welcome Aboard,'} <br/><span className="text-emerald-500">{t('verify_success_title_span') || 'Captain!'}</span>
                   </h2>
                   <p className="text-slate-500 font-medium leading-relaxed px-4">{message}</p>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={() => navigate('/')} 
                    className="w-full h-16 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase tracking-[0.2em] text-xs rounded-2xl shadow-2xl shadow-orange-900/40 active:scale-[0.98] transition-all group"
                  >
                    {t('btn_go_dashboard') || 'GO TO DASHBOARD'} <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </motion.div>
            )}

            {status === 'error' && (
              <motion.div 
                key="error"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="space-y-8"
              >
                <div className="flex justify-center text-rose-500">
                   <XCircle size={64} className="drop-shadow-xl" />
                </div>
                <div className="space-y-3">
                   <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic">{t('verify_error_title') || 'Something went wrong'}</h2>
                   <p className="text-slate-500 font-medium leading-relaxed px-4">{message}</p>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={() => navigate('/')} 
                    variant="outline"
                    className="w-full h-16 border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-slate-900 dark:text-white font-black uppercase tracking-widest text-[10px] rounded-2xl"
                  >
                    {t('btn_return_home') || 'RETURN HOME'}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Branding */}
        <p className="mt-10 text-center text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 opacity-50">
          {t('security_protocol') || 'BüroFuchs Security Protocol v4.0'}
        </p>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;